'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import SiteHeader from '@/components/SiteHeader';
import SiteFooter from '@/components/SiteFooter';
import PageHeader from '@/components/shared/PageHeader';
import SearchBar from '@/components/shared/SearchBar';
import FilterRow from '@/components/shared/FilterRow';
import Badge from '@/components/shared/Badge';
import EmptyState from '@/components/shared/EmptyState';
import { ResidentCardSkeleton } from '@/components/shared/Skeletons';
import { mockResidents, Resident } from '@/data/mockResidents';
import { Users, Star, Lock, ArrowRight } from 'lucide-react';

const filters = [
  { label: 'All', value: 'all' },
  { label: 'Owner', value: 'Owner' },
  { label: 'Renter', value: 'Renter' },
  { label: 'Resident', value: 'Resident' },
  { label: 'Multiple Unit Owner', value: 'Multiple Unit Owner' },
  { label: 'Tower 1', value: 'tower1' },
  { label: 'Tower 2', value: 'tower2' },
  { label: 'Services Offered', value: 'services' },
];

function getRoleBadgeVariant(role: Resident['role']) {
  if (role === 'Owner') return 'owner' as const;
  if (role === 'Renter') return 'renter' as const;
  if (role === 'Multiple Unit Owner') return 'multipleUnitOwner' as const;
  return 'resident' as const;
}

function AvatarInitials({ name, size = 'md' }: { name: string; size?: 'sm' | 'md' | 'lg' }) {
  const initials = name.split(' ').map((n) => n[0]).join('').slice(0, 2).toUpperCase();
  const dim = size === 'lg' ? 'w-14 h-14 text-[16px]' : size === 'md' ? 'w-12 h-12 text-[15px]' : 'w-9 h-9 text-[11px]';
  return (
    <div
      className={`${dim} rounded-full flex items-center justify-center flex-shrink-0 font-display font-bold`}
      style={{
        background: 'linear-gradient(135deg, rgba(76,201,255,0.20), rgba(122,92,255,0.20))',
        border: '1px solid rgba(76,201,255,0.20)',
        color: '#4CC9FF',
      }}
    >
      {initials}
    </div>
  );
}

// Featured residents — opt-in, service providers and professionals
const featuredResidents = mockResidents.filter((r) =>
  r.servicesOffered && r.servicesOffered.length > 0 && r.isVerified
).slice(0, 4);

function FeaturedResidentCard({ resident }: { resident: Resident }) {
  return (
    <Link href={`/directory/${resident.slug}`} className="block group focus-visible:outline-none">
      <div
        className="neon-card p-5 h-full group-focus-visible:border-[rgba(76,201,255,0.35)]"
        style={{ border: '1px solid rgba(240,168,50,0.15)', background: 'rgba(240,168,50,0.03)' }}
      >
        <div className="flex items-start gap-3 mb-3">
          <AvatarInitials name={resident.name} size="lg" />
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <h3 className="font-display font-semibold text-[14px] leading-tight" style={{ color: '#F2F4FF' }}>
                {resident.name}
              </h3>
              <span
                className="flex-shrink-0 text-[10px] px-1.5 py-0.5 rounded font-semibold uppercase tracking-wide"
                style={{ background: 'rgba(240,168,50,0.12)', color: '#F0A832', border: '1px solid rgba(240,168,50,0.22)' }}
              >
                Featured
              </span>
            </div>
            <div className="flex flex-wrap items-center gap-1.5 mt-1">
              <Badge variant={getRoleBadgeVariant(resident.role)} label={resident.role} />
              {resident.vouchedBy && <Badge variant="vouched" label="Vouched" />}
            </div>
          </div>
        </div>

        {resident.profession && (
          <p className="text-[13px] font-medium mb-2" style={{ color: 'rgba(242,244,255,0.80)' }}>
            {resident.profession}
          </p>
        )}

        {resident.servicesOffered && resident.servicesOffered.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-2">
            {resident.servicesOffered.slice(0, 2).map((s) => (
              <span
                key={s}
                className="text-[11px] px-2 py-0.5 rounded"
                style={{ background: 'rgba(122,92,255,0.10)', color: '#9B7FFF', border: '1px solid rgba(122,92,255,0.18)' }}
              >
                {s}
              </span>
            ))}
            {resident.servicesOffered.length > 2 && (
              <span className="text-[11px]" style={{ color: '#A7A3C4' }}>
                +{resident.servicesOffered.length - 2} more
              </span>
            )}
          </div>
        )}

        <div className="mt-3 flex items-center gap-1 text-[12px] font-medium" style={{ color: '#F0A832' }}>
          View profile <ArrowRight size={11} />
        </div>
      </div>
    </Link>
  );
}

function ResidentCard({ resident }: { resident: Resident }) {
  return (
    <Link href={`/directory/${resident.slug}`} className="block group focus-visible:outline-none">
      <div className="neon-card p-5 cursor-pointer h-full group-focus-visible:border-[rgba(76,201,255,0.35)] group-focus-visible:shadow-[0_0_0_3px_rgba(76,201,255,0.15)]">
        <div className="flex items-start gap-4 mb-3">
          <AvatarInitials name={resident.name} />
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <h3 className="font-display font-semibold text-[15px] leading-tight" style={{ color: '#F2F4FF' }}>
                {resident.name}
              </h3>
              {resident.isSponsor && <Badge variant="sponsor" />}
            </div>
            <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
              <Badge variant={getRoleBadgeVariant(resident.role)} label={resident.role} />
              {resident.vouchedBy && <Badge variant="vouched" label="Vouched" />}
            </div>
          </div>
        </div>

        <div className="space-y-1 mb-3">
          <p className="text-[12.5px]" style={{ color: '#A7A3C4' }}>
            {resident.tower} · Unit {resident.unit}
          </p>
          {resident.profession && (
            <p className="text-[13px] font-medium" style={{ color: 'rgba(242,244,255,0.75)' }}>
              {resident.profession}
            </p>
          )}
        </div>

        {resident.servicesOffered && resident.servicesOffered.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-3">
            {resident.servicesOffered.slice(0, 2).map((s) => (
              <span
                key={s}
                className="text-[11px] px-2 py-0.5 rounded"
                style={{ background: 'rgba(122,92,255,0.10)', color: '#9B7FFF', border: '1px solid rgba(122,92,255,0.18)' }}
              >
                {s}
              </span>
            ))}
            {resident.servicesOffered.length > 2 && (
              <span className="text-[11px]" style={{ color: '#A7A3C4' }}>
                +{resident.servicesOffered.length - 2} more
              </span>
            )}
          </div>
        )}

        {resident.vouchedBy && (
          <p className="mt-3 text-[11.5px]" style={{ color: 'rgba(45,212,160,0.80)' }}>
            Vouched by {resident.vouchedBy}
          </p>
        )}
      </div>
    </Link>
  );
}

// Community stats for public teaser
const communityStats = [
  { label: 'Verified Residents', value: `${mockResidents.length}+` },
  { label: 'Owners', value: `${mockResidents.filter((r) => r.role === 'Owner' || r.role === 'Multiple Unit Owner').length}` },
  { label: 'Renters & Residents', value: `${mockResidents.filter((r) => r.role === 'Renter' || r.role === 'Resident').length}` },
  { label: 'Offering Services', value: `${mockResidents.filter((r) => r.servicesOffered && r.servicesOffered.length > 0).length}` },
];

export default function DirectoryPage() {
  const [search, setSearch] = useState('');
  const [activeFilter, setActiveFilter] = useState('all');
  const [isLoading] = useState(false);

  // Mock: not approved — show teaser state
  const isApproved = false;

  const filtered = mockResidents.filter((r) => {
    const matchesSearch =
      !search ||
      r.name.toLowerCase().includes(search.toLowerCase()) ||
      r.profession?.toLowerCase().includes(search.toLowerCase()) ||
      r.tower.toLowerCase().includes(search.toLowerCase());

    const matchesFilter =
      activeFilter === 'all' ||
      (activeFilter === 'tower1' && r.tower === 'Tower 1') ||
      (activeFilter === 'tower2' && r.tower === 'Tower 2') ||
      (activeFilter === 'services' && r.servicesOffered && r.servicesOffered.length > 0) ||
      r.role === activeFilter;

    return matchesSearch && matchesFilter;
  });

  return (
    <div className="min-h-screen" style={{ background: '#07060C' }}>
      <SiteHeader theme="dark" />
      <main className="pt-16">
        <PageHeader
          eyebrow="Resident Directory"
          title="Panorama Residents"
          description="Connect with verified neighbors across Tower 1 and Tower 2. Contact details are private by default — residents choose what to share."
          stat={{ value: `${mockResidents.length}`, label: 'Verified residents' }}
        />

        {/* Private launch teaser banner */}
        {!isApproved && (
          <div
            className="border-b"
            style={{ background: 'rgba(122,92,255,0.04)', borderColor: 'rgba(122,92,255,0.15)' }}
          >
            <div className="max-w-screen-2xl mx-auto px-6 lg:px-10 py-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <Lock size={14} style={{ color: '#9B7FFF', flexShrink: 0 }} />
                <p className="text-[13px] leading-relaxed" style={{ color: 'rgba(167,163,196,0.80)' }}>
                  <span style={{ color: '#F2F4FF' }}>Resident Directory</span> is part of the private Panorama network. Full profiles and contact details are visible to verified residents only.
                </p>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <Link href="/request-access" className="inline-flex items-center gap-1.5 text-[12px] font-semibold px-3 py-1.5 rounded-lg transition-all duration-150" style={{ background: 'rgba(122,92,255,0.15)', border: '1px solid rgba(122,92,255,0.30)', color: '#C4BEFF' }}>
                  Request Access <ArrowRight size={11} />
                </Link>
                <Link href="/login" className="text-[12px] font-medium px-3 py-1.5 rounded-lg transition-all duration-150" style={{ color: 'rgba(167,163,196,0.60)', border: '1px solid rgba(255,255,255,0.08)' }}>
                  Sign In
                </Link>
              </div>
            </div>
          </div>
        )}

        <div className="max-w-screen-2xl mx-auto px-6 lg:px-10 py-8">

          {/* Community stats — public teaser */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-10">
            {communityStats.map((stat) => (
              <div
                key={stat.label}
                className="neon-card p-4 text-center"
              >
                <p className="font-display font-bold text-[22px] mb-1" style={{ color: '#F2F4FF' }}>{stat.value}</p>
                <p className="text-[11px] font-medium uppercase tracking-wide" style={{ color: 'rgba(167,163,196,0.50)' }}>{stat.label}</p>
              </div>
            ))}
          </div>

          {/* Featured Residents */}
          {activeFilter === 'all' && !search && (
            <div className="mb-10">
              <div className="flex items-center gap-3 mb-2">
                <Star size={15} style={{ color: '#F0A832' }} />
                <h2 className="font-display font-semibold text-[16px]" style={{ color: '#F2F4FF' }}>
                  Featured Residents
                </h2>
                <div className="flex-1 h-px" style={{ background: 'rgba(255,255,255,0.06)' }} />
              </div>
              <p className="text-[12.5px] mb-4" style={{ color: 'rgba(167,163,196,0.55)' }}>
                Opt-in featured profiles — real estate agents, resident-owned businesses, and service providers who have chosen to be highlighted.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {featuredResidents.map((r) => (
                  <FeaturedResidentCard key={r.id} resident={r} />
                ))}
              </div>
              <p className="text-[11px] mt-3" style={{ color: 'rgba(167,163,196,0.30)' }}>
                Featured placement is opt-in. Interested in being featured? Contact the platform team once you&apos;re verified.
              </p>
            </div>
          )}

          {/* Private directory notice */}
          <div
            className="flex items-start gap-3 px-4 py-3.5 rounded-xl mb-8"
            style={{ background: 'rgba(76,201,255,0.04)', border: '1px solid rgba(76,201,255,0.12)' }}
          >
            <Lock size={14} style={{ color: '#4CC9FF', marginTop: 2, flexShrink: 0 }} />
            <div>
              <p className="text-[13px] font-medium mb-0.5" style={{ color: '#F2F4FF' }}>
                Full directory is visible to verified residents only
              </p>
              <p className="text-[12px]" style={{ color: '#A7A3C4' }}>
                Contact details, unit numbers, and full profiles are private. Residents control what they share.{' '}
                <Link href="/request-access" className="underline hover:opacity-80 transition-opacity" style={{ color: '#4CC9FF' }}>
                  Request access to join.
                </Link>
              </p>
            </div>
          </div>

          {/* Search + Filters */}
          <div className="flex flex-col sm:flex-row gap-4 mb-6">
            <div className="flex-1 max-w-sm">
              <SearchBar
                placeholder="Search by name, profession, tower..."
                value={search}
                onChange={setSearch}
              />
            </div>
          </div>
          <div className="mb-8">
            <FilterRow filters={filters} active={activeFilter} onChange={setActiveFilter} />
          </div>

          {/* Loading state */}
          {isLoading && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {Array.from({ length: 8 }).map((_, i) => (
                <ResidentCardSkeleton key={i} />
              ))}
            </div>
          )}

          {/* No results */}
          {!isLoading && filtered.length === 0 && (
            <EmptyState
              title="No residents found"
              description="Try adjusting your search or filter to find who you're looking for."
              icon={<Users size={22} style={{ color: '#4CC9FF' }} />}
              action={
                <button
                  onClick={() => { setSearch(''); setActiveFilter('all'); }}
                  className="btn-secondary text-[13px] px-4 py-2"
                >
                  Clear filters
                </button>
              }
            />
          )}

          {/* Populated grid — blurred for non-approved */}
          {!isLoading && filtered.length > 0 && (
            <>
              <p className="text-[13px] mb-5" style={{ color: '#A7A3C4' }}>
                {filtered.length} resident{filtered.length !== 1 ? 's' : ''}
                {activeFilter !== 'all' || search ? ' matching your filters' : ''}
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" style={!isApproved ? { filter: 'blur(4px)', pointerEvents: 'none', userSelect: 'none', opacity: 0.5 } : {}}>
                {filtered.map((r) => (
                  <ResidentCard key={r.id} resident={r} />
                ))}
              </div>
              {!isApproved && (
                <div className="text-center mt-6">
                  <p className="text-[13px] mb-3" style={{ color: 'rgba(167,163,196,0.60)' }}>
                    Full resident profiles are visible after verification.
                  </p>
                  <Link href="/request-access" className="btn-primary inline-flex items-center gap-2 text-[13px]">
                    <Users size={14} />
                    Request Resident Access
                    <ArrowRight size={13} />
                  </Link>
                </div>
              )}
            </>
          )}

          {/* Cross-links */}
          <div
            className="mt-16 pt-8 border-t"
            style={{ borderColor: 'rgba(255,255,255,0.06)' }}
          >
            <p className="text-[11px] font-semibold tracking-[0.10em] uppercase mb-4" style={{ color: 'rgba(167,163,196,0.35)' }}>
              Also in the community
            </p>
            <div className="flex flex-wrap gap-3">
              <Link href="/services" className="text-[13px] font-medium px-4 py-2 rounded-lg transition-all duration-150 hover:bg-white/[0.06]" style={{ color: '#A7A3C4', border: '1px solid rgba(255,255,255,0.08)' }}>
                Resident Services
              </Link>
              <Link href="/conversations" className="text-[13px] font-medium px-4 py-2 rounded-lg transition-all duration-150 hover:bg-white/[0.06]" style={{ color: '#A7A3C4', border: '1px solid rgba(255,255,255,0.08)' }}>
                Conversations
              </Link>
              <Link href="/marketplace" className="text-[13px] font-medium px-4 py-2 rounded-lg transition-all duration-150 hover:bg-white/[0.06]" style={{ color: '#A7A3C4', border: '1px solid rgba(255,255,255,0.08)' }}>
                Marketplace
              </Link>
              <Link href="/events" className="text-[13px] font-medium px-4 py-2 rounded-lg transition-all duration-150 hover:bg-white/[0.06]" style={{ color: '#A7A3C4', border: '1px solid rgba(255,255,255,0.08)' }}>
                Events
              </Link>
              <Link href="/resources" className="text-[13px] font-medium px-4 py-2 rounded-lg transition-all duration-150 hover:bg-white/[0.06]" style={{ color: '#A7A3C4', border: '1px solid rgba(255,255,255,0.08)' }}>
                Resources
              </Link>
            </div>
          </div>
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}
